import { Graphics, Text, _ReactPixi } from '@inlet/react-pixi';
import { CustomPixiContainer, AnswerComponent, BorderComponent } from 'components';
import { useBubble, useTextAnimation } from 'hooks';
import { createAnswersTextStyle, createBubbleTextStyle, getPosTool } from 'lib/tools';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getGlobal, getPersonsList } from 'store';
import * as PIXI from 'pixi.js';
import { GAP, INTERVAL, NAME_TEXT_SIZE, PADDING } from 'custom-constants';

export const BubbleWrapper: FC = () => {
  const { height, width } = useSelector(getGlobal);
  const { currentBubble, nextBubble } = useBubble();
  const personList = useSelector(getPersonsList);
  const { animatedText, skipAnimation } = useTextAnimation(currentBubble.text, INTERVAL);

  const textRef = useRef<_ReactPixi.IText>(null);
  const [textHeight, setTextHeight] = useState(0);
  const [textWidth, setTextWidth] = useState(0);

  // Container Params
  const containerPositionX = 0;
  const containerPositionY = 0;
  const containerWidth = width;
  const containerHeight = height;

  // Content Params
  const contentWidth = width * 0.8;
  const contentPadding = PADDING * (currentBubble.name ? 1.2 : 1);
  const contentHeight =
    textHeight + contentPadding + NAME_TEXT_SIZE + (currentBubble.name ? 20 : 10);
  const isInteractive = currentBubble.options.length === 1 || animatedText !== currentBubble.text;

  // Answers Params
  const [answersSize, setAnswersSize] = useState([0]);

  const answersWidth = width * 0.8;
  const sumAnswersSize = answersSize.reduce((a, b) => a + b);

  // useEffect(() => {
  //   setAnswersSize([0]);
  // }, [currentBubble.options.length, currentBubble.text]);

  const bubblePosX = useMemo(() => getPosTool('center', width, width * 0.8, 1), [width]);

  const bubblePosY = useMemo(
    () => getPosTool('end', height, contentHeight, 2),
    [height, contentHeight]
  );

  const style = useMemo(() => createBubbleTextStyle(contentWidth), [contentWidth]);

  const styleAnswer = useMemo(
    () => createAnswersTextStyle(contentWidth, answersWidth),
    [contentWidth, answersWidth]
  );

  // fake graphic for cover content in container, to accept click
  const draw = useCallback(
    (g: PIXI.Graphics) => {
      g.clear();
      g.beginFill(0xffffff, 1 / 1e6);
      g.drawRect(0, 0, width, height);
    },
    [width, height]
  );

  const handlePointerDownNext = useCallback(() => {
    const ev = new CustomEvent('storyClick');
    window.dispatchEvent(ev);
    if (animatedText !== currentBubble.text) {
      skipAnimation();
    } else if (currentBubble.options.length === 1) {
      nextBubble(0);
    }
  }, [
    animatedText,
    currentBubble.options.length,
    currentBubble.text,
    nextBubble,
    skipAnimation,
    contentHeight,
  ]);

  return (
    <CustomPixiContainer
      width={width}
      height={height}
      x={0}
      y={0}
      interactive={isInteractive}
      buttonMode={isInteractive}
      pointerdown={handlePointerDownNext}
    >
      <Graphics draw={draw} />
      {currentBubble.text ? (
        <CustomPixiContainer
          height={containerHeight}
          width={containerWidth}
          x={containerPositionX}
          y={containerPositionY}
        >
          <BorderComponent
            {...{
              contentWidth,
              contentHeight,
              sumAnswersSize,
              textHeight,
              text: currentBubble.text,
              name: currentBubble.speaker ? personList[currentBubble.speaker].name : '',
              namePosition: currentBubble.speaker
                ? currentBubble.persons.find((person) => person.personId === currentBubble.speaker)
                    ?.position
                : 'left',
              bubblePosY,
              bubblePosX,
            }}
          >
            <Text
              text={animatedText}
              style={style}
              x={PADDING}
              y={contentPadding}
              width={Math.min(textWidth, contentWidth)}
              ref={(e) => {
                setTextHeight(e?.texture?.orig?.height || 0);
                setTextWidth(e?.texture?.orig?.width || 0);
                return textRef;
              }}
            />
          </BorderComponent>
        </CustomPixiContainer>
      ) : null}
      {currentBubble.options.filter(Boolean).length > 1
        ? currentBubble.options.filter(Boolean).map((o, id) => (
            <AnswerComponent
              key={o}
              style={styleAnswer}
              text={o}
              width={answersWidth}
              setAnswersSize={setAnswersSize}
              visible={animatedText === currentBubble.text}
              x={bubblePosX}
              y={
                bubblePosY +
                contentHeight -
                (currentBubble.name ? 10 : 0) +
                GAP * id - // gap между блоками
                answersSize.reduce((a, b, idx) => {
                  if (id < idx) {
                    return a + b;
                  }
                  return a;
                })
              }
              p={PADDING * 1.8}
              onNext={() => {
                setAnswersSize([0]);
                nextBubble(id);
              }}
              currentBubble={currentBubble}
            />
          ))
        : null}
    </CustomPixiContainer>
  );
};
